import type { ReactNode } from 'react';
import { Box } from '@zillow/constellation-10';

/**
 * Gives a page-level {@link ErrorPage} a definite, viewport-filling height so it centers
 * within the area left over by the app shell's chrome (header + footer) instead of
 * collapsing. `ErrorPage` itself renders at `height: 100%`; it needs a sized parent, and
 * that height differs only by which chrome each app shell shows. This container owns the
 * shared chrome heights in one place so they aren't copy-pasted as magic numbers across apps.
 *
 * Wrap a single `ErrorPage` (404/500/ErrorBoundary fallback). Pick the `layout` that matches
 * the app shell around it; set `bodyMargin` only if the app hasn't reset the browser default
 * body margin.
 *
 * @example // left-nav app (desktop nav is a sidebar, no top header)
 * <ErrorPageContainer>
 *   <ErrorPage errorMessage="We can’t find that page" action={<a href="/">Go to Rental Manager</a>} />
 * </ErrorPageContainer>
 *
 * @example // app that serves both header-nav and left-nav routes — decide per route
 * <ErrorPageContainer layout={usesHeaderNav() ? 'header-nav' : 'left-nav'}>
 *   <ErrorPage ... />
 * </ErrorPageContainer>
 *
 * **Chrome heights** (shared app shell — change them here, not in each app):
 * - mobile header `55px`, desktop header `82px` (header-nav layouts only)
 * - footer `112px` (base) / `120px` (md and up)
 * - body margin `16px` (only when `bodyMargin` is set)
 */
export type ErrorPageContainerProps = {
  children: ReactNode;
  /**
   * Which app-shell layout sits around the error page on desktop, so the container subtracts
   * the right header height at the `xxl` breakpoint:
   * - `'header-nav'` — keeps a top header on desktop (renter-hub / colandlord layouts); subtracts `82px`.
   * - `'left-nav'` (default) — desktop nav is a left sidebar with no top header; subtracts nothing.
   *
   * On `base`/`md` both layouts show the same `55px` mobile header, so this only affects `xxl`.
   * Apps that serve both layouts should compute it per route, e.g.
   * `layout={usesHeaderNav() ? 'header-nav' : 'left-nav'}`.
   */
  layout?: 'header-nav' | 'left-nav';
  /**
   * Set when the app hasn't reset the browser's default body margin, so the container subtracts
   * the extra `16px` and the full-height page doesn't overflow into a scrollbar. Most apps reset
   * the body margin and can omit this.
   */
  bodyMargin?: boolean;
};

export function ErrorPageContainer({ children, layout = 'left-nav', bodyMargin = false }: ErrorPageContainerProps) {
  return (
    <Box
      css={{
        // Literal calc strings (and ternaries of literals) so Panda statically extracts every
        // possible value at build time — do NOT interpolate the pixel values from variables.
        height: {
          base: bodyMargin ? 'calc(100dvh - 55px - 112px - 16px)' : 'calc(100dvh - 55px - 112px)',
          md: bodyMargin ? 'calc(100dvh - 55px - 120px - 16px)' : 'calc(100dvh - 55px - 120px)',
          xxl:
            layout === 'header-nav'
              ? bodyMargin
                ? 'calc(100dvh - 82px - 120px - 16px)'
                : 'calc(100dvh - 82px - 120px)'
              : bodyMargin
                ? 'calc(100dvh - 120px - 16px)'
                : 'calc(100dvh - 120px)',
        },
      }}
    >
      {children}
    </Box>
  );
}
